import { createSelector } from "@ngrx/store";
import { AppState } from "@store/app.state";
import { range } from "lodash-es";
import { RedactionStatus } from "@models/redaction-status.enum";
import { LifeCycleEdit } from "src/app/components/review/redaction-annotation/life-cycle-edit.enum";

const selectFeature = (state: AppState) => state.docViewer?.redactionEditor;

export const currentRedactionEditor = () =>
    createSelector(
        selectFeature,
        (state) => {
            return state;
        }
    );

export const currentRedactionId = () =>
    createSelector(
        selectFeature,
        (state) => {
            return state?.currentRedactionId;
        }
    );

export const currentRedactionStatus = () =>
    createSelector(
        selectFeature,
        (state) => {
            return state?.status;
        }
    );

export const allRedactions = createSelector(
    selectFeature,
    (state) => {
        return state?.redactions;
    }
);

export const currentActiveRedactions = createSelector(
    selectFeature,
    (state) => {
        return state?.redactions.filter(e => e.status != LifeCycleEdit.Deleted);
    }
);

export const currentRedactionEditorPage = () =>
    createSelector(
        selectFeature,
        (state) => {
            return state.pageNumber;
        }
    );

export const currentDocTotalPageCount = () =>
    createSelector(
        selectFeature,
        (state) => {
            return state.totalPages;
        }
    );

export const currentFileId = () =>
    createSelector(
        selectFeature,
        (state) => {
            return state.fileIdList[state.pageNumber];
        }
    );

export const redactionsForCurrentPage = () =>
    createSelector(
        currentActiveRedactions, currentRedactionEditorPage(),
        (redactions, pageNumber) => {
            return redactions.filter(x => x.rank === pageNumber)
        }
    );

export const pagesAsAnArray = () =>
    createSelector(
        currentDocTotalPageCount(),
        (totalPages) => {
            return range(1, totalPages + 1);
        }
    );

export const redactionEditorMode = () =>
    createSelector(
        currentRedactionStatus(), isEditorProcessing,
        (status, isProcessing) => {
            if (status === RedactionStatus.None) {
                return 'generate-images';
            } else if (isProcessing) {
                return 'loading';
            } else if ((status & RedactionStatus.HasImages) || (status & RedactionStatus.HasPdf)) {
                return 'redact-images';
            }
            return 'loading';
        }
    );

export const maxRedactionId = createSelector(
    allRedactions,
    (redactions) => {
        return redactions.reduce((max, redaction) => {
            return Math.max(max, redaction.clientId);
        }, 0);
    }
);

export const docHasRedactionImages = createSelector(
    currentRedactionStatus(),
    (status) => {
        if ((status & RedactionStatus.HasImages) || (status & RedactionStatus.HasPdf)) {
            return true;
        }
        return false;
    }
);

export const isEditorProcessing = createSelector(
    currentRedactionStatus(),
    (status) => {
        if ((status & RedactionStatus.ImagesCreationRequested)
            || (status & RedactionStatus.ImagesProcessing)
            || (status & RedactionStatus.PdfCreationRequested)
            || (status & RedactionStatus.PdfProcessing)) {
            return true;
        }
        return false;
    }
);

export const isManualRedaction = createSelector(
    currentRedactionStatus(),
    (status) => status === RedactionStatus.HasPdf);

export const hasPdfAndNativeRedaction = createSelector(
    currentRedactionStatus(),
    status => (status & RedactionStatus.HasPdf) !== 0 &&
        (status & RedactionStatus.HasRedactedNative) !== 0);
